import { FC, useMemo } from "react"
import { Loader2 } from "lucide-react"
import Table from "../../table"
import DescriptionCell from "./cells/description"
import TimestampCell from "./cells/timestamp"
import EmptyState from "../../empty-state"
import RefetchButton from "../refetch-button"
import ActivityFilter from "../filter"
import DateFilter, { DateRangePreset } from "../date-filter"
import LoanOfficerFilter from "../loan-officer-filter"
import SingleRowList from "../../skeletons/single-row-list"
import { EXCLUDED_ACTIVITY_TYPES } from "../../../constants"
import { Activity, BBColumnDef } from "../../../../types"
import { getActivityRoute } from "../hooks/useActivityNavigation"

interface Props {
  isLoading: boolean
  data?: Activity[]
  isBrandActivity?: boolean
  refetch?: () => void
  loadMoreRef?: React.RefObject<HTMLDivElement>
  isLoadingMore?: boolean
  hasMore?: boolean
  selectedEventTypes?: string[]
  onFilterChange?: (types: string[]) => void
  onClearFilters?: () => void
  selectedDateRange?: DateRangePreset
  customStartDate?: Date | null
  customEndDate?: Date | null
  onDateRangeChange?: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDateFilter?: () => void
  // Loan officer filter (brand admin only)
  isBrandAdmin?: boolean
  orgId?: string
  selectedLoanOfficer?: string | null
  onLoanOfficerChange?: (loid: string | null) => void
  hideFilters?: boolean
}

const ActivityTable: FC<Props> = ({
  data,
  isLoading,
  isBrandActivity,
  refetch,
  loadMoreRef,
  isLoadingMore = false,
  hasMore = false,
  selectedEventTypes = [],
  onFilterChange,
  onClearFilters,
  selectedDateRange = "all",
  customStartDate = null,
  customEndDate = null,
  onDateRangeChange,
  onClearDateFilter,
  isBrandAdmin = false,
  orgId,
  selectedLoanOfficer,
  onLoanOfficerChange,
  hideFilters = false
}) => {
  const columns: BBColumnDef<Activity>[] = [
    {
      id: "description",
      header: "Description",
      accessorFn: row => row,
      footer: props => props.column.id,
      cell: props => (
        <DescriptionCell
          info={props.row.original}
          isBrandActivity={isBrandActivity}
        />
      ),
      colSpan: 3
    },

    {
      id: "timestamp",
      header: "Timestamp",
      accessorFn: row => row.timestamp,
      footer: props => props.column.id,
      cell: props => <TimestampCell info={props.row.original} />
    },
    {
      id: "type",
      header: "Type",
      accessorFn: row => row.type,
      footer: props => props.column.id,
      cell: () => null,
      size: 0
    }
  ]

  const filteredActivities = useMemo(() => {
    if (data?.length && !isLoading) {
      return data!.filter(
        activity => !(activity.type in EXCLUDED_ACTIVITY_TYPES)
      )
    }
    return []
  }, [data, isLoading])

  return (
    <div className="py-6 bg-white">
      {/* Custom header with filters and refetch */}
      {!hideFilters && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          {/* Left side: Filters */}
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            {/* Event Type Filter */}
            {onFilterChange && onClearFilters && (
              <ActivityFilter
                selectedTypes={selectedEventTypes}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
              />
            )}

            {/* Date Range Filter */}
            {onDateRangeChange && onClearDateFilter && (
              <DateFilter
                selectedRange={selectedDateRange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onRangeChange={onDateRangeChange}
                onClearFilter={onClearDateFilter}
              />
            )}

            {/* Loan Officer Filter (Brand Admin only) */}
            {isBrandAdmin && orgId && onLoanOfficerChange && (
              <LoanOfficerFilter
                orgId={orgId}
                selectedLoanOfficer={selectedLoanOfficer ?? null}
                onChange={onLoanOfficerChange}
              />
            )}
          </div>

          {/* Right side: Refetch Button */}
          {refetch && (
            <div className="flex justify-end">
              <RefetchButton onClick={refetch} />
            </div>
          )}
        </div>
      )}

      {/* Show empty state if no data and not loading */}
      {!data?.length && !isLoading ? (
        <div className="px-6">
          <EmptyState
            heading="No activities yet"
            subHeading="Try adjusting your filters or check back later"
          />
        </div>
      ) : (
        <Table
          data={filteredActivities}
          isLoading={isLoading}
          columns={columns}
          viewType="table"
          pageSize={loadMoreRef ? 999999 : 10}
          noSorter
          noHeader
          searchBarVisible={false}
          defaultSortBy="timestamp"
          defaultSortDir="desc"
          loaderSkeleton={<SingleRowList />}
          tableContainerClassName="!overflow-visible"
          dividedRows={false}
          getRowProps={row => {
            const isClickable = !!getActivityRoute(row.original)
            return {
              className: isClickable
                ? "cursor-pointer hover:bg-slate-50 transition-colors duration-150"
                : ""
            }
          }}
        />
      )}

      {/* Infinite scroll sentinel and loading states */}
      {loadMoreRef && (
        <>
          {/* Intersection observer target */}
          <div ref={loadMoreRef} className="h-4" />

          {/* Loading more indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
              <span className="ml-2 text-sm text-slate-600">
                Loading more activities...
              </span>
            </div>
          )}

          {/* No more data indicator */}
          {!hasMore && !isLoadingMore && filteredActivities.length > 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="h-px w-12 bg-slate-300" />
                <span>You've reached the end</span>
                <div className="h-px w-12 bg-slate-300" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ActivityTable
